import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';
import range from 'lodash/range';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';

import {TIMEZONES} from 'common/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  FunnelQueryResponse,
  isSegmentedData,
  SegmentedFunnelResults,
  UnsegmentedFunnelResult,
} from 'common/report/funnels/models/api-types';
import {BasicFunnelStep, FunnelBookmark, FunnelStep} from 'common/report/funnels/models/types';
import {BarVizEl} from 'common/charts/visualization/bar-viz';
import {SegmentFilterController, SegmentSection} from 'common/charts/contexts/segment-filter-controller';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {
  addConversionRateToFunnel,
  bookmarkHasLiftComparison,
  bookmarkHasTimeComparison,
  BucketValueInfo,
  createBarChartPopoverSelection,
  FunnelSegmentExtras,
  generateLegendSections,
  getBarChartDataFromRawFunnel,
  getBucketCount,
  getDefaultSortConfig,
  getStepIndexFromBookmark,
  sortSegments,
} from 'common/report/funnels/util/bar-chart';
import {Bar, BarClickEvent, BarSegment, SegmentWithoutChildren} from 'common/charts/visualization/bar-viz/types';
import {ChartTooltipAttrs} from 'common/components/chart-tooltip';
import {getBucketData, getFunnelStep, getToolTipSections} from 'common/report/funnels/models/bar-chart/utils';
import {
  FunnelStepBucketAccessors,
  getLiftToolTipText,
  getToolTipText,
} from 'common/report/funnels/models/frequency-chart/utils';
import {getCalendarDateRangeLabels, getCalendarDateRanges} from 'common/report/funnels/models/utils';
import {mapBookmarkStepsToEntries} from 'common/report/funnels/models/format';
import {getAlphaLabelFromIndex} from 'common/util';
import {isGroupedEntry} from 'common/query-builder/query-entry-section/types';
import {ChartType, REPORT_LIMITS, ReportName, SavedLegend} from 'common/report/constants';
import {FrequencyViewType} from 'common/report/funnels/models/frequency-chart/types';
import {getBookmarkWithAppliedDefaults} from 'common/report/funnels/util/bookmark-overrides';

import css from './index.inline.styl';

interface FrequencyDistributionChartAttrs {
  'project-as-of-date': string;
  'project-timezone': typeof TIMEZONES[number];
  'hide-tooltip'?: boolean;
  'enable-bar-click'?: boolean;
}

interface FrequencyDistributionChartState {
  stepIndex: number | null;
}

// TODO(sam) support any order steps and grouped steps
export class FunnelsFrequencyDistributionChart extends Component<
  FrequencyDistributionChartState,
  FrequencyDistributionChartAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  private _funnelsResponse: FunnelQueryResponse;
  private _bookmark: FunnelBookmark;

  static get attrsSchema(): AttrsSchema<FrequencyDistributionChartAttrs> {
    return {
      ...super.attrsSchema,
      'hide-tooltip': `boolean`,
      'project-as-of-date': `string`,
      'project-timezone': `string`,
      'enable-bar-click': `boolean`,
    };
  }

  connectedCallback() {
    // Have to call processData after creation of the element in the DOM tree (so the context can be grabbed from parent),
    // but before first render
    this.processData();

    super.connectedCallback();
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  get config(): ConfigOptions<FrequencyDistributionChartState, any, ChartsContextRegistry> {
    return {
      useShadowDom: true,
      css,
      contexts: [`segmentFilterController` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      template: () => this.renderTemplate(),
    };
  }

  public set funnelsResponse(response: FunnelQueryResponse) {
    if (!isEqual(this._funnelsResponse, response)) {
      this._funnelsResponse = response;

      this.processData();
    }
  }

  public set bookmark(bookmark: FunnelBookmark) {
    const updatedBookmark = getBookmarkWithAppliedDefaults(bookmark);
    if (!isEqual(this._bookmark, updatedBookmark)) {
      this._bookmark = updatedBookmark;
      this.processData();
    }
  }

  private processData() {
    if (!(this._funnelsResponse && this._bookmark && this.isConnected)) {
      return;
    }

    const {sections, savedLegend} = generateLegendSections(this._funnelsResponse, this._bookmark);
    this.initSegFilterController(this.segmentFilterController, sections, savedLegend);

    addConversionRateToFunnel(this._funnelsResponse, this._bookmark);

    this.update();
  }

  private renderTemplate() {
    const selectedSections = this.getSelectedSegments(this.segmentFilterController);

    const segments = getBarChartDataFromRawFunnel({
      funnelsResponse: this._funnelsResponse,
      selectedSections,
      bookmark: this._bookmark,
    });

    sortSegments(segments, this._funnelsResponse, this._bookmark, getDefaultSortConfig(selectedSections.length));

    return (
      <div sel='.frequency-distribution-widget'>
        <BarVizEl
          attrs={{
            'enable-bar-click-events': this.attr(`enable-bar-click`),
            'hide-tooltip': this.attr(`hide-tooltip`),
          }}
          props={{
            chartData: segments,
            tooltipDataCallback: this.createTooltipDataCallback(),
          }}
          on={{
            barClick: (ev: CustomEvent<BarClickEvent<FunnelSegmentExtras & BucketValueInfo>>) => {
              const {x, y} = this.getBoundingClientRect();
              const {segment, barSegment, pageX, pageY} = ev.detail;
              const selection = createBarChartPopoverSelection({
                segment,
                barSegment,
                pageX,
                pageY,
                x,
                y,
                scrollX: window.scrollX,
                scrollY: window.scrollY,
              });
              const comparisonDateRanges = getCalendarDateRanges(
                this._bookmark,
                this._funnelsResponse,
                this.attr(`project-as-of-date`),
                this.attr(`project-timezone`),
              );
              if (selection) {
                this.dispatchEvent(
                  new CustomEvent(`openPopover`, {
                    detail: {
                      selection,
                      comparisonDateRange: selection.isComparison ? comparisonDateRanges[1] : null,
                    },
                  }),
                );
              }
            },
          }}
        />
      </div>
    );
  }

  createTooltipDataCallback() {
    const steps = mapBookmarkStepsToEntries(this._bookmark.steps);
    const stepIndex = getStepIndexFromBookmark(this._bookmark);
    const step = stepIndex === null ? steps[steps.length - 1] : steps[stepIndex];
    let stepName;

    // TODO(sam) this partially supports any order/groups but not _really_, make it actually
    if (isGroupedEntry(step)) {
      stepName = range(0, step.event.length)
        .map((idx) => `${stepIndex + 1}${getAlphaLabelFromIndex(idx)}`)
        .join(` or `);
    } else {
      stepName = step.event.renamed || step.event.label;
    }

    return (segment: SegmentWithoutChildren, _bar: Bar, barSegment: BarSegment<BucketValueInfo>): ChartTooltipAttrs => {
      const dataBar = barSegment;

      let compareBar;
      let compareSections;
      const hasCompareData = bookmarkHasTimeComparison(this._bookmark);
      const hasLiftData = bookmarkHasLiftComparison(this._bookmark);
      if (hasCompareData && !hasLiftData) {
        compareBar = segment.bars[1].segments[0];
        const compareData = Object.values(this._funnelsResponse.compare_data)[0] as SegmentedFunnelResults;
        compareSections = getToolTipText(
          this._bookmark.frequencyType !== FrequencyViewType.ConversionRate
            ? compareBar.extras.count
            : this.getConversionCount(
                this.findMatchingSegmentData(compareData, segment),
                stepIndex,
                dataBar.extras.bucketIdx,
              ),
          compareBar.extras.percentage,
          segment.label,
          !(this._bookmark.frequency_buckets.bucket_size === 1 && dataBar.extras.bucketIdx === 0),
          stepName,
          this._bookmark.frequencyType,
        );
      }

      const originalBar = segment.bars[0].segments[0];
      const originalData = this._funnelsResponse.data[this._funnelsResponse.meta.dates[0]];
      let originalCompareData;
      if (hasCompareData) {
        originalCompareData = Object.values(this._funnelsResponse.compare_data)[0];
      }

      let liftComparisonCount;
      if (hasLiftData) {
        liftComparisonCount = originalBar.extras.liftComparisonCount;

        if (hasCompareData && this._bookmark.frequencyType === FrequencyViewType.ConversionRate) {
          liftComparisonCount = this.getConversionCount(
            this.findMatchingSegmentData(originalCompareData, segment),
            stepIndex,
            dataBar.extras.bucketIdx,
          );
        }
      }
      const sections = this.generateTooltipSections(
        hasLiftData,
        originalBar,
        originalData,
        segment,
        stepIndex,
        dataBar,
        liftComparisonCount,
        stepName,
      );

      return {
        ...(hasCompareData ? {} : {'title-color': barSegment.color}),
        sections: getToolTipSections(
          segment.extras.parentLabel,
          barSegment.color,
          sections,
          compareSections,
          getCalendarDateRangeLabels(
            this._bookmark,
            this._funnelsResponse,
            this.attr(`project-as-of-date`),
            this.attr(`project-timezone`),
          ),
        ),
      };
    };
  }

  private generateTooltipSections(
    hasLiftData: boolean,
    originalBar: BarSegment<any>,
    originalData,
    segment: SegmentWithoutChildren<any>,
    stepIndex: number,
    dataBar: BarSegment<BucketValueInfo>,
    liftComparisonCount,
    stepName,
  ) {
    if (hasLiftData) {
      return getLiftToolTipText({
        count:
          this._bookmark.frequencyType !== FrequencyViewType.ConversionRate
            ? originalBar.extras.count
            : this.getConversionCount(
                this.findMatchingSegmentData(originalData, segment),
                stepIndex,
                dataBar.extras.bucketIdx,
              ),
        percentage: originalBar.extras.percentage,
        liftComparisonCount,
        bucketLabel: segment.label,
        segmentName: segment.extras.parentLabel,
        baselineSegmentName: originalBar.extras.baselineSegmentName,
        plural: !(this._bookmark.frequency_buckets.bucket_size === 1 && dataBar.extras.bucketIdx === 0),
        stepName,
        liftType: this._bookmark.liftComparison?.type,
        dateRanges: getCalendarDateRangeLabels(
          this._bookmark,
          this._funnelsResponse,
          this.attr(`project-as-of-date`),
          this.attr(`project-timezone`),
        ),
      });
    } else {
      return getToolTipText(
        this._bookmark.frequencyType !== FrequencyViewType.ConversionRate
          ? originalBar.extras.count
          : this.getConversionCount(
              this.findMatchingSegmentData(originalData, segment),
              stepIndex,
              dataBar.extras.bucketIdx,
            ),
        originalBar.extras.percentage,
        segment.label,
        !(this._bookmark.frequency_buckets.bucket_size === 1 && dataBar.extras.bucketIdx === 0),
        stepName,
        this._bookmark.frequencyType,
      );
    }
  }

  findMatchingSegmentData(data: SegmentedFunnelResults | UnsegmentedFunnelResult, segment: SegmentWithoutChildren) {
    const segmentData = data;
    let funnelSegment: Array<BasicFunnelStep | FunnelStep>;
    if (isSegmentedData(segmentData)) {
      const path = segment.extras.segmentPath;
      let currentData = segmentData;
      path.forEach((pathSection: {id: string | number}) => {
        const intermediatePath = currentData[pathSection.id];
        isArray(intermediatePath) ? (funnelSegment = intermediatePath) : (currentData = intermediatePath);
      });
    } else {
      funnelSegment = segmentData.steps;
    }
    return funnelSegment;
  }

  getConversionCount(funnelSegment: FunnelStep[], stepIndex: number, bucketIndex: number) {
    const funnelStep = getFunnelStep(funnelSegment, stepIndex);
    const bucketData = getBucketData(funnelStep, FunnelStepBucketAccessors.ConvertStepFreqBuckets);
    return getBucketCount(bucketData, bucketIndex);
  }

  initSegFilterController(
    segmentFilterController: SegmentFilterController,
    sections: SegmentSection[],
    savedLegend: SavedLegend[],
  ) {
    segmentFilterController.loadSections(sections, {
      defaultNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelFrequencyBar].default,
      maxNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelFrequencyBar].max,
    });
    segmentFilterController.loadFromSavedLegend(savedLegend);
  }

  getSelectedSegments(segmentFilterController: SegmentFilterController) {
    const selectedSections = segmentFilterController
      .getSections()
      .map((segmentSection: SegmentSection, i) => ({
        ...segmentSection,
        items: segmentSection.items
          .filter((v) => segmentFilterController.isItemSelected(i, v.id))
          .map((v) => ({...v, color: segmentFilterController.getItemColor(i, v.id)})),
      }))
      // Reverse is because we want the deepest segmentation to be the first in the list
      .reverse();
    return selectedSections;
  }
}

export const FunnelsFrequencyDistributionChartWidgetEl = defineAndMakeJsxFactory(
  `mp-funnels-frequency-distribution-chart-widget`,
  FunnelsFrequencyDistributionChart,
);

export const FunnelsFrequencyDistributionChartWidgetReact = panel2react(
  `mp-funnels-frequency-distribution-chart-widget`,
  FunnelsFrequencyDistributionChart,
);
